import React, { useState, useEffect, useMemo } from 'react';
import { func, shape, string } from 'prop-types';

import get from 'lodash/get';
import { Image } from 'nordic/image';
import { LoggerFactory } from 'nordic/logger';
import { CarouselSnapped, CarouselSnappedSlide } from '@andes/carousel-snapped';

import APIServiceImage from '../../../../../../services/picturesCarousel';
import Link from '../../../../../components-v2/link';
import { namespace } from '../../../../../components-v2/layout/constants';

const log = LoggerFactory('infowindow_image_component');

/**
 * @typedef {Object} Picture
 * @property {string} key - Unique identifier key for the picture
 * @property {string} id - ID of the picture
 * @property {string} normal - URL of the normal size image
 * @property {string} [alt] - Alternative text for the image
 */

/**
 * @typedef {Object} PicturesStack
 * @property {string} [normal] - URL of the normal size image
 * @property {string} [retina] - URL of the retina size image
 */

/**
 * @typedef {Object} ItemPictures
 * @property {PicturesStack} [stack] - Stack of image URLs in different resolutions
 */

/**
 * @typedef {Object} ItemData
 * @property {string} title - Title of the item
 * @property {string} permalink - URL link for the item
 * @property {string} id - Unique identifier for the item
 * @property {ItemPictures} [pictures] - Main picture of the item
 */

/**
 * ImageComponent
 *
 * Renders a carousel of images for an item in the infowindow.
 * Displays the main image immediately and fetches additional images asynchronously.
 * Provides navigation arrows when multiple images are available.
 *
 * @param {Object} props - Component props
 * @param {ItemData} props.item - Item data containing image information
 * @param {Function} [props.onClick] - Handler for click events on the images
 * @returns {React.ReactElement} Rendered image carousel component
 */

const ImageComponent = ({ item, onClick }) => {
  const { title, permalink, id, pictures: itemMainPicture } = item;
  const [carouselPictures, setCarouselPictures] = useState([]);
  const [loading, setLoading] = useState(true);

  /**
   * Creates the initial main image object from item data
   * @type {Picture|null}
   */
  const initialMainImage = useMemo(() => {
    const mainImageUrl = get(itemMainPicture, 'stack.retina') || get(itemMainPicture, 'stack.normal');

    return mainImageUrl
      ? {
          key: `main-${id}`,
          id: `main-${id}`,
          normal: mainImageUrl,
          alt: title,
        }
      : null;
  }, [itemMainPicture, id, title]);

  /**
   * Fetches additional carousel pictures and combines with main image
   */
  useEffect(() => {
    if (initialMainImage) {
      setCarouselPictures([initialMainImage]);
    }

    setLoading(true);

    let isMounted = true;

    const fetchCarouselPictures = async () => {
      try {
        const { pictures } = await APIServiceImage.getCarouselPictures(id, undefined);

        if (isMounted) {
          const fetchedPictures = pictures || [];
          const additionalPictures = fetchedPictures.slice(1);

          const combinedPictures = initialMainImage ? [initialMainImage, ...additionalPictures] : additionalPictures;

          setCarouselPictures(combinedPictures);
        }
      } catch (error) {
        log.error(`Error fetching carousel pictures for itemId: ${id}`, error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    if (initialMainImage) {
      fetchCarouselPictures();
    } else {
      setLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [id, initialMainImage]);

  const showArrows = !loading && carouselPictures.length > 1;

  return (
    <div className={`${namespace}__infowindow-image-carousel`}>
      <CarouselSnapped
        type="full"
        pagination={false}
        arrows={{ visibility: showArrows ? 'always' : 'never', size: 'small' }}
        lazyload={false}
      >
        {carouselPictures.map((picture) => (
          <CarouselSnappedSlide key={picture.key || picture.id || picture.normal}>
            <Link
              key={picture.key || picture.id || picture.normal}
              className={`${namespace}__infowindow-image-link`}
              href={permalink}
              title={title}
              isInternal={false}
              target="_blank"
              onClick={onClick}
            >
              <Image src={picture.normal} alt={picture.alt || title} lazyload="off" carousel />
            </Link>
          </CarouselSnappedSlide>
        ))}
      </CarouselSnapped>
    </div>
  );
};

ImageComponent.propTypes = {
  item: shape({
    title: string.isRequired,
    permalink: string.isRequired,
    id: string.isRequired,
    pictures: shape({
      stack: shape({
        normal: string,
      }),
    }),
  }).isRequired,
  onClick: func,
};

ImageComponent.defaultProps = {
  onClick: undefined,
};

export { ImageComponent };
